import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import {
  Home,
  Info,
  Scale,
  Users,
  Phone,
  Mail,
  MapPin,
  Layers,
} from "lucide-react";

export default function Footer() {
  // Google Maps link for Wave One Tower, Noida Sector 18
  const mapsUrl = "https://www.google.com/maps/search/?api=1&query=Wave+One+Tower+Sector+18+Noida";
  
  return (
    <footer className="relative bg-[#111827] text-white overflow-hidden">
      {/* SUBTLE DARK OVERLAY */}
      <div className="absolute inset-0 bg-black/20" />

      <div className="relative max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-4 md:grid-cols-2 gap-14">
        
        {/* ================= BRAND ================= */}
        <div>
          <h3 className="text-white text-2xl font-playfair font-bold">
            Blackthorn Chambers
          </h3>

          <p className="text-xs uppercase tracking-widest text-yellow-400 mt-1">
            Advocates & Legal Consultants
          </p>

          <p className="mt-5 text-sm leading-relaxed text-white">
            Providing exceptional legal services with integrity,
            professionalism, and dedication to achieving the best outcomes
            for our clients.
          </p>

          <div className="mt-6 h-[2px] w-20 bg-yellow-400" />
        </div>

        {/* ================= QUICK LINKS ================= */}
        <div>
          <h4 className="text-white text-lg font-semibold mb-5">
            Quick Links
          </h4>
          <ul className="space-y-3 text-sm">
            <li>
              <Link 
                to="/" 
                className="flex items-center gap-3 text-white hover:text-yellow-400 transition"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <Home size={16} /> Home
              </Link>
            </li>
            <li>
              <Link 
                to="/about" 
                className="flex items-center gap-3 text-white hover:text-yellow-400 transition"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <Info size={16} /> About Us
              </Link>
            </li>
            <li>
              <Link 
                to="/practice-areas" 
                className="flex items-center gap-3 text-white hover:text-yellow-400 transition"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <Scale size={16} /> Practice Areas
              </Link>
            </li>
            <li>
              <Link 
                to="/team" 
                className="flex items-center gap-3 text-white hover:text-yellow-400 transition"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <Users size={16} /> Our commitment
              </Link>
            </li>
            <li>
              <Link 
                to="/contact" 
                className="flex items-center gap-3 text-white hover:text-yellow-400 transition"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                <Phone size={16} /> Contact Us
              </Link>
            </li>
          </ul>
        </div>

        {/* ================= PRACTICE AREAS (PARENT NAMES ONLY) ================= */}
        <div>
          <h4 className="text-white text-lg font-semibold mb-5">
            Legal Services
          </h4>

          <ul className="space-y-3 text-sm text-white">
            <li className="flex items-start gap-3">
              <Layers size={16} className="text-yellow-400 mt-1" />
              Multi-Disciplinary Legal Services
            </li>
            <li className="flex items-start gap-3">
              <Layers size={16} className="text-yellow-400 mt-1" />
              Full-Spectrum Legal Services
            </li>
            <li className="flex items-start gap-3">
              <Layers size={16} className="text-yellow-400 mt-1" />
              Multi-Disciplinary Litigation & Advisory Practice
            </li>
            <li className="flex items-start gap-3">
              <Layers size={16} className="text-yellow-400 mt-1" />
              End-to-End Legal Representation
            </li>
            <li className="flex items-start gap-3">
              <Layers size={16} className="text-yellow-400 mt-1" />
              Pan-India Legal Practice
            </li>
            <li className="flex items-start gap-3">
              <Layers size={16} className="text-yellow-400 mt-1" />
              Integrated Litigation, Advisory & Compliance
            </li>
            <li className="flex items-start gap-3">
              <Layers size={16} className="text-yellow-400 mt-1" />
              Criminal, Civil & Commercial Law Expertise
            </li>
            <li className="flex items-start gap-3">
              <Layers size={16} className="text-yellow-400 mt-1" />
              Specialised Litigation & Regulatory Practice
            </li>
            <li className="flex items-start gap-3">
              <Layers size={16} className="text-yellow-400 mt-1" />
              Complete Legal Solutions Under One Roof
            </li>
          </ul>
        </div>

        {/* ================= CONTACT ================= */}
        <div>
          <h4 className="text-white text-lg font-semibold mb-5">
            Contact Us
          </h4>

          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-3 text-sm text-white leading-relaxed hover:text-yellow-400 transition-colors cursor-pointer group"
          >
            <MapPin size={18} className="text-yellow-400 mt-1 group-hover:scale-110 transition-transform" />
            <span className="underline-offset-2 group-hover:underline">
              Blackthorn Chambers <br />
              Office Suite# 18, 18th Floor, <br />
              Gold Tower, Wave One Tower, <br />
              L-2A, Pocket G, Sector 18, <br />
              Noida, Uttar Pradesh 201301
            </span>
          </a>

          <p className="mt-4 flex items-center gap-3 text-sm text-white">
            <Phone size={16} className="text-yellow-400" />
            <a href="tel:+917303269472" className="hover:text-yellow-400 transition">
              +91 73032 69472
            </a>
          </p>

          <p className="mt-2 flex items-center gap-3 text-sm text-white">
            <Mail size={16} className="text-yellow-400" />
            <a href="mailto:blackthornchambers@gmail.com" className="hover:text-yellow-400 transition">
              blackthornchambers@gmail.com
            </a>
          </p>

          <a
            href="https://wa.me/917303269472?text=Hello%2C%20I%20would%20like%20to%20inquire%20about%20legal%20services"
            target="_blank"
            rel="noopener noreferrer"
            className="
              inline-flex items-center gap-2 mt-6
              px-6 py-2
              border border-yellow-400
              text-yellow-400
              font-semibold text-sm
              hover:bg-yellow-400 hover:text-[#111827]
              transition
            "
          >
            Get Legal Consultation →
          </a>
        </div>
      </div>

      {/* ================= COPYRIGHT ================= */}
      <div className="relative border-t border-white/10 py-5 text-center text-sm text-white">
        © {new Date().getFullYear()} Blackthorn Chambers. All Rights Reserved.
      </div>
    </footer>
  );
}
